import debounce from 'debounce';

import { addCustomEvents, dispatchCustomEvent } from './utilities/custom-events';
import { getParameter } from './utilities/url';
import cellScenarios from './scenarios/cell';
import campaignIds from './campaign-ids';
import {
    getCell,
    getLgc,
    getSpid,
    setCell,
    setLgc,
    setSpid
} from './session-storage';
import replacements from './replacements';
import { fetchFields } from './utilities';
import { contentWaitTimeout } from "./utilities/static";
import { initCtaGuard } from './cta-guard';
import './blocks/apply-now-button/index';
import './blocks/apply-now-button/frontend';

class DisneyRewardsTrackingCodes {
    fields = false;

    // 1 minute, 60,000 milliseconds. Default wait time before making another request to refresh data.
    requestTimeout = 60000;

    constructor() {
        this.bindEvents();

        initCtaGuard();

        window.localStorage.setItem(
            'disneyrewards_tracking_codes',
            JSON.stringify({
                fetched: 'true'
            })
        );

        (async () => {
            this.fields = await fetchFields();
            if (!this.fields) {
                return;
            }

            // Validate stored SPID codes before refreshing — redirects if invalid.
            this.validateSpidCodes();

            // Refresh tracking codes.
            this.refreshCodes();

            // Replace tokens.
            this.replaceTokens(document);

            // Dispatch an event for other components that need to rely on this object's availability.
            dispatchCustomEvent('trackingCodesReady');
        })();
    }

    /**
     * Attaches event handlers for this integration.
     */
    bindEvents() {
        addCustomEvents([
            {
                name: 'blur',
                callback: debounce(this.onWindowChange.bind(this), this.requestTimeout)
            },
            {
                name: 'cardTypeChange',
                callback: this.onCardTypeChange.bind(this)
            },
            {
                name: 'coverflowCarouselReady',
                callback: this.onCoverflowCarouselReady.bind(this)
            },
            {
                name: 'crraFootnotesChange',
                callback: this.onCrraFootnotesChange.bind(this)
            },
            {
                name: 'crraLocationChange',
                callback: this.onCrraLocationChange.bind(this)
            },
            {
                name: 'filmstripCarouselReady',
                callback: this.onFilmstripCarouselReady.bind(this)
            },
            {
                name: 'focus',
                callback: debounce(this.onWindowChange.bind(this), this.requestTimeout)
            },
            {
                name: 'footnotesProcessed',
                callback: this.onFootnotesProcessed.bind(this)
            },
            {
                name: 'updateApplyNow',
                callback: this.onUpdateApplyNow.bind(this)
            }
        ]);
    }

    onCardTypeChange(e) {
        const newCardType = e.detail.cardType;

        const applylNowLinks = document.querySelectorAll(`.apply-now__links[data-for-card-type=${newCardType}`);
        if (applylNowLinks.length) {
            applylNowLinks.forEach((applyNowLink) => {
                this.replaceTokens(applyNowLink);
            });
        }

        // Only perform an update if the current CELL type is default.
        if (!['none', 'default'].includes(getCell().type)) {
            return;
        }

        setCell({
            value: this.fields.cell.default[newCardType],
            type: newCardType
        });
    }

    onCoverflowCarouselReady(e) {
        this.replaceTokens(e.detail.element);
    }

    onCrraFootnotesChange() {
        setTimeout(() => {
            const footnotes = document.querySelector('.footnotes');

            if (footnotes) {
                this.replaceTokens(footnotes);
            }
        }, contentWaitTimeout);
    }

    onCrraLocationChange() {
        setTimeout(() => {
            this.replaceTokens(document.getElementById('crra'));
        }, contentWaitTimeout);
    }

    onFilmstripCarouselReady(e) {
        this.replaceTokens(e.detail.element);
    }

    onFootnotesProcessed() {
        const footnotes = document.querySelector('.footnotes');

        if (footnotes) {
            this.replaceTokens(footnotes);
        }
    }

    onUpdateApplyNow(e) {
        this.replaceTokens(e.detail.element.parentElement);
    }

    async onWindowChange(e) {
        // Only allow 'blur' event to run the code again on Safari due to issues with the 'focus' event in that browser.
        if (e.type === 'blur' && !window.navigator.userAgent.includes('Safari')) {
            return;
        }

        // Refresh fields information from the CMS.
        this.fields = await fetchFields();
        if (!this.fields) {
            return;
        }

        // Refresh tracking codes.
        this.refreshCodes();

        // Replace tokens and cellCode in links.
        this.replaceTokens(document);
    }

    /**
     * Validates the SPID values stored in sessionStorage against the valid SPID
     * values from the tracking codes configuration. If any stored value is a
     * 4-character alphanumeric code that does not appear in the valid set,
     * clears sessionStorage and redirects to the expired offer page.
     */
    validateSpidCodes() {
        const storedSpid = getSpid();

        // Skip validation if no SPID has been set yet.
        if (storedSpid.campaignId === 'none') {
            return;
        }

        const { defaultSet, campaignSets } = this.fields.spid;
        const themeSettingsCampaignSets = Array.isArray(campaignSets) ? campaignSets : [];
        const allCampaignSets = [defaultSet, ...themeSettingsCampaignSets];

        // Collect all unique valid SPID values from the configuration.
        const validSpidValues = new Set();
        for (const set of allCampaignSets) {
            for (const cardType of ['classic', 'premier', 'inspire']) {
                if (Object.hasOwn(set, cardType) && set[cardType]) {
                    validSpidValues.add(set[cardType]);
                }
            }
        }

        // Check if any stored SPID value is a valid 4-character code that
        // does not match the current tracking codes configuration.
        const spidPattern = /^[A-Z0-9]{4}$/;
        for (const cardType of ['classic', 'premier', 'inspire']) {
            if (!Object.hasOwn(storedSpid, cardType)) {
                continue;
            }
            const storedValue = storedSpid[cardType];
            if (storedValue && spidPattern.test(storedValue) && !validSpidValues.has(storedValue)) {
                sessionStorage.clear();
                window.location.href = '/expired-offer-page-interstitial/notice/expired-offer-page';
                return;
            }
        }
    }

    /**
     * Refresh the current CELL code by testing through different scenarios in order.
     */
    refreshCell() {
        for (let i = 0; i < cellScenarios.length; i += 1) {
            const scenario = cellScenarios[i];
            const condition = scenario.condition();

            if (condition) {
                if (scenario.action(this.fields)) {
                    break;
                }
            }
        }
    }

    /**
     * Refresh all tracking codes.
     */
    refreshCodes() {
        this.refreshSpid();
        this.refreshLgc();
        this.refreshCell();
    }

    /**
     * Refresh the current LGC code set.
     */
    refreshLgc() {
        const lgc = getLgc();

        const { defaultSet, campaignSets } = this.fields.lgc;

        // Get the default campaign set as defined in the WordPress Admin Theme
        // Settings, or use fallback values.
        const defaultCampaignSet = defaultSet;

        // Get all campaign sets defined in the WordPress Admin Theme Settings.
        // Ensure it's always an array to prevent spread operator errors
        // Handle cases where campaignSets might be null, undefined, an object, or not an array
        const themeSettingsCampaignSets = Array.isArray(campaignSets) ? campaignSets : [];

        // Get all campaign sets together to allow searching/filtering to
        // incorporate the default value.
        const allCampaignSets = [defaultCampaignSet, ...themeSettingsCampaignSets];

        // Use the first campaign set whose id matches the current 'pub' query
        // param if one is provided.
        const currentCmpParam = getParameter('pub');
        if (currentCmpParam.key !== null || lgc.campaignId === campaignIds.egift) {
            const campaign = currentCmpParam.value || campaignIds.paidSearch;
            const requestedCampaignSet = allCampaignSets.filter(
                (campaignSet) => campaignSet.campaignId === campaign
            )[0];

            if (requestedCampaignSet) {
                setLgc(requestedCampaignSet);
                return;
            }
        }

        // If not present, use the default LGC's.
        if (lgc.campaignId === 'none') {
            setLgc(defaultCampaignSet);
        }
    }

    /**
     * Merges a campaign set with the default set, using default values as fallbacks
     * for empty classic, premier, and inspire fields.
     * @param {object} campaignSet The campaign set to merge
     * @param {object} defaultSet The default campaign set to use as fallback
     * @returns {object} The merged campaign set
     */
    mergeCampaignSetWithDefault(campaignSet, defaultSet) {
        if (!campaignSet || !defaultSet) {
            return campaignSet || defaultSet;
        }

        return {
            campaignId: campaignSet.campaignId || defaultSet.campaignId || '',
            classic: (campaignSet.classic && campaignSet.classic.trim() !== '')
                ? campaignSet.classic
                : (defaultSet.classic || ''),
            premier: (campaignSet.premier && campaignSet.premier.trim() !== '')
                ? campaignSet.premier
                : (defaultSet.premier || ''),
            inspire: (campaignSet.inspire && campaignSet.inspire.trim() !== '')
                ? campaignSet.inspire
                : (defaultSet.inspire || ''),
            forceCellCode: campaignSet.forceCellCode || ''
        };
    }

    /**
     * Refresh the current SPID campaign.
     */
    refreshSpid() {
        const spid = getSpid();

        const { defaultSet, campaignSets } = this.fields.spid;

        // Get the default campaign set as defined in the WordPress Admin Theme Settings.
        const defaultCampaignSet = defaultSet;

        // Get all campaign sets defined in the WordPress Admin Theme Settings.
        // Ensure it's always an array to prevent spread operator errors
        // Handle cases where campaignSets might be null, undefined, an object, or not an array
        const themeSettingsCampaignSets = Array.isArray(campaignSets) ? campaignSets : [];

        // Get all campaign sets together to allow searching/filtering to
        // incorporate the default value.
        const allCampaignSets = [defaultCampaignSet, ...themeSettingsCampaignSets];

        // Use the first campaign set whose id matches the current 'pub' query
        // param if one is provided.
        const currentCmpParam = getParameter('pub');
        if (currentCmpParam.key !== null || spid.campaignId === campaignIds.egift) {
            const campaign = currentCmpParam.value ?? campaignIds.paidSearch;
            const requestedCampaignSet = allCampaignSets.filter((campaignSet) => campaignSet.campaignId === campaign);

            if (requestedCampaignSet.length) {
                const mergedSet = this.mergeCampaignSetWithDefault(requestedCampaignSet[0], defaultCampaignSet);
                setSpid(mergedSet);
                return;
            }
        }

        // Check if a campaign is selected in page metadata and use it.
        const { selectedSpidCampaign } = this.fields.page || {};
        if (selectedSpidCampaign) {
            const selectedCampaignSet = allCampaignSets.find(
                (campaignSet) => campaignSet.campaignId === selectedSpidCampaign
            );

            if (selectedCampaignSet) {
                const mergedSet = this.mergeCampaignSetWithDefault(selectedCampaignSet, defaultCampaignSet);
                setSpid(mergedSet);
                return;
            }
        }

        // If not present, use the default SPID.
        if (spid.campaignId === 'none') {
            setSpid(defaultCampaignSet);
        }
    }

    /**
     * Perform a token replacement on token values.
     * @param {HTMLElement} context The HTML element to searching within the DOM.
     */
    replaceTokens(context = document) {
        const tokenSelectorString = replacements
            .map((replacement) =>
                replacement
                    .patterns()
                    .map((pattern) => `a[href*="${pattern}"]`)
                    .join(', ')
            )
            .join(', ');
        const elements = Array.from(context.querySelectorAll(tokenSelectorString));

        if (elements.length) {
            elements.forEach((element) => {
                // Get the string in need of replacement.
                let str = decodeURI(element.href);

                // Perform the replacement on each token.
                replacements.forEach((replacement) => {
                    const value = replacement.value();

                    replacement.patterns().forEach((pattern) => {
                        str = str.replace(pattern, value);
                    });
                });

                // Update the element attribute with the replaced string.
                element.setAttribute('href', str);

                dispatchCustomEvent('replaceTokens', {
                    element
                });
            });
        }
    }
}

export const initializeDisneyRewardsTrackingCodes = () => {
    if (window.disneyRewards === undefined) {
        window.disneyRewards = {};
    }

    if (window.disneyRewards.globals === undefined) {
        window.disneyRewards.globals = {};
    }

    window.disneyRewards.globals.trackingCodes = new DisneyRewardsTrackingCodes();
};
